import type { AgentRunSummary } from "@common/schemas/domains/agentRuns/agent-run.schema";
import type {
    TaskActivityEvent,
    TaskActivityPayload,
} from "@common/schemas/domains/agentRuns/task-activity.schema";
import { AgentRunState, AgentRunWaitKind } from "@common/types/agentRun";
import { ActorType } from "@common/types/auditLog";
import type { DemoRunKind, DemoRunRecord } from "./busyClosesDemo.types";
import type { DemoIdFactory, DemoRandom } from "./demoRandom";
import { getDemoRunScript } from "./demoRunScripts";
import type { DemoClient, DemoEntity, DemoPerson } from "./demoSources";

const MINUTE_MS = 60_000;
const EVENT_IDS_PER_RUN = 16;
const AUXILIARY_ID_OFFSET = 0x800;

const RUN_STATES: Record<DemoRunKind, AgentRunState> = {
    working: AgentRunState.running,
    waitingOnClient: AgentRunState.waiting,
    paused: AgentRunState.running,
    needsReview: AgentRunState.waiting,
    finished: AgentRunState.done,
};

interface BuildDemoRunArgs {
    kind: DemoRunKind;
    /** The run's place in its dataset, which picks its ids. */
    index: number;
    ids: DemoIdFactory;
    random: DemoRandom;
    now: number;
    runTaskId: string;
    taskTitle: string;
    client: DemoClient;
    entity: DemoEntity;
    close: AgentRunSummary["close"];
    closeTasks: AgentRunSummary["closeTasks"];
    startedBy: DemoPerson;
    reviewer: DemoPerson;
    month: string;
    account: string;
}

/** A task activity event on a fake run, from the agent when `actorId` is null. */
export const toDemoEvent = ({
    id,
    run,
    payload,
    actorId,
    createdAt,
}: {
    id: string;
    run: Pick<AgentRunSummary, "id" | "workspaceId" | "runTaskId">;
    payload: TaskActivityPayload;
    actorId: number | null;
    createdAt: number;
}): TaskActivityEvent => ({
    id,
    workspaceId: run.workspaceId,
    activityScope: { taskId: run.runTaskId, agentRunId: run.id },
    actorType: actorId === null ? ActorType.AGENT : ActorType.USER,
    actorId,
    dedupeKey: null,
    createdAt: new Date(createdAt).toISOString(),
    ...payload,
});

/** A full fake run in one of the five states, with its history and the events the ticker appends later. */
export const buildDemoRun = (args: BuildDemoRunArgs): DemoRunRecord => {
    const { kind, index, ids, random, now, client, entity, reviewer } = args;
    const waitedOnTaskId = ids("task", AUXILIARY_ID_OFFSET + index);
    const eventIdAt = (eventIndex: number) =>
        ids("event", index * EVENT_IDS_PER_RUN + eventIndex);
    const waitedOnTitle =
        kind === "waitingOnClient"
            ? `Missing receipts from ${client.name}`
            : `Review ${args.month} categorization for ${entity.name}`;
    const script = getDemoRunScript({
        kind,
        waitedOnTaskId,
        copy: {
            month: args.month,
            account: args.account,
            clientName: client.name,
            entityName: entity.name,
            reviewerName: reviewer.firstName,
            waitedOnTitle,
        },
    });
    const newestAt = now - random.int(4, 40) * MINUTE_MS;
    const stepMinutes = random.int(25, 55);
    const createdAtOf = (eventIndex: number) =>
        newestAt -
        (script.history.length - 1 - eventIndex) * stepMinutes * MINUTE_MS;
    const startedAt = createdAtOf(0);
    const runId = ids("run", index);
    const runRef = {
        id: runId,
        workspaceId: client.workspaceId,
        runTaskId: args.runTaskId,
    };
    const events = script.history
        .map((event, eventIndex) =>
            toDemoEvent({
                id: eventIdAt(eventIndex),
                run: runRef,
                payload: event.payload,
                actorId: event.isHuman ? reviewer.id : null,
                createdAt: createdAtOf(eventIndex),
            }),
        )
        .toReversed();
    const isWaiting = kind === "waitingOnClient" || kind === "needsReview";
    const run: AgentRunSummary = {
        id: runId,
        workspaceId: client.workspaceId,
        workspaceName: client.name,
        runTaskId: args.runTaskId,
        entityUuid: entity.uuid ?? ids("entity", index),
        entity: {
            name: entity.name,
            iconColor: entity.iconColor,
            logo: entity.logo,
            isPersonal: false,
        },
        close: args.close,
        state: RUN_STATES[kind],
        pausedAt: kind === "paused" ? new Date(newestAt).toISOString() : null,
        finishReason: null,
        stage: {
            key: script.stageLabel.toLowerCase(),
            label: script.stageLabel,
        },
        stateLine: null,
        startedBy: {
            id: args.startedBy.id,
            name: args.startedBy.name,
            avatar: args.startedBy.avatar,
            iconColor: args.startedBy.iconColor,
            email: args.startedBy.email,
        },
        closeTasks: args.closeTasks,
        progress: {
            ...script.progress,
            at: new Date(newestAt).toISOString(),
            segment: script.history.length,
        },
        waitingOn: isWaiting
            ? [
                  {
                      taskId: waitedOnTaskId,
                      threadId: ids("fallback", AUXILIARY_ID_OFFSET + index),
                      kind:
                          kind === "needsReview"
                              ? AgentRunWaitKind.proposal
                              : AgentRunWaitKind.answer,
                      question: null,
                      title: waitedOnTitle,
                  },
              ]
            : [],
        counters: {
            segmentCount: script.history.length,
            stepCount: script.history.length * 6,
            toolInvocationCount: script.history.length * 4,
        },
        startedByUserId: args.startedBy.id,
        createdAt: new Date(startedAt).toISOString(),
        updatedAt: new Date(newestAt).toISOString(),
    };
    return {
        run,
        taskTitle: args.taskTitle,
        events,
        queue: script.queue.map(({ isHuman, ...step }, queueIndex) => ({
            ...step,
            eventId: eventIdAt(script.history.length + queueIndex),
            actorId: isHuman ? reviewer.id : null,
        })),
        lastEventAt: newestAt,
    };
};
